/*
* Copyright 2026 Tecnalia, Basque Research & Technology Alliance (BRTA)
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*
*  SPDX-License-Identifier: Apache-2.0
*/

import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { AppConfigService } from '../../core/services/app-config/app-config.service';
import { AuthGuardData, createAuthGuard } from 'keycloak-angular';
import { AuthService } from '../services/auth.service';
import { AuthStatus } from '../models/auth-status';
import { inject } from '@angular/core';

const AUTH_FEEDBACK_NAVIGATION_STATE_KEY = 'authFeedbackNavigation';

function navigateToAuthFeedback(router: Router, path: string): false {
	void router.navigateByUrl(path, {
		state: { [AUTH_FEEDBACK_NAVIGATION_STATE_KEY]: true }
	});
	return false;
}

const isAccessAllowed = async (
	route: ActivatedRouteSnapshot,
	_: RouterStateSnapshot,
	_authData: AuthGuardData
): Promise<boolean | UrlTree> => {

	const router = inject(Router);
	const authSvc = inject(AuthService);
	const configSvc = inject(AppConfigService);

	const authStatus: AuthStatus = authSvc.getAuthenticationStatus();

	if (authStatus === AuthStatus.LOGGED_IN) {

		const requiredRoles: string[] = route.data['roles'] || [];

		const hasRequiredRoles = authSvc.checkRealmRolesIncludeAnyOfRequiredRoles(requiredRoles);

		if (hasRequiredRoles) { return true; } else {
			return navigateToAuthFeedback(router, configSvc.settings.accessDeniedUrlPath);
		}

	} else if (authStatus === AuthStatus.LOGGED_OUT) {

		authSvc.login();
		return false;

	} else if (authStatus === AuthStatus.ERROR) {

		return navigateToAuthFeedback(router, configSvc.settings.loginErrorUrlPath);

	}

	return navigateToAuthFeedback(router, configSvc.settings.accessDeniedUrlPath);

};

const demoModeCanActivateFn: CanActivateFn = () => true;

export const canActivateAuthRole: CanActivateFn = (route, state) => {

	const configSvc = inject(AppConfigService);

	if (configSvc.settings.demoMode) {
		return demoModeCanActivateFn(route, state);
	}
	
	return createAuthGuard<CanActivateFn>(isAccessAllowed)(route, state);

};

export const canActivateAuthFeedback: CanActivateFn = () => {
	const router = inject(Router);
	const configSvc = inject(AppConfigService);
	const navigation = router.getCurrentNavigation();
	const isInternalFeedbackNavigation =
		navigation?.extras.state?.[AUTH_FEEDBACK_NAVIGATION_STATE_KEY] === true;

	return isInternalFeedbackNavigation ? true : router.parseUrl(configSvc.settings.rootUrlPath);
};